import Section from "$/components/Section";
import MaskDrawingGuide from "$/pages/tools/MaskDrawingGuide";
import { FontDownload } from "@mui/icons-material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CreateIcon from "@mui/icons-material/DesignServices";
import IdeaIcon from "@mui/icons-material/EmojiObjects";
import CoverupIcon from "@mui/icons-material/FlipToFront";
import FillerIcon from "@mui/icons-material/FormatColorFill";
import OutlineIcon from "@mui/icons-material/HighlightAlt";
import { Box, Container, MenuItem, MenuList, Stack, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import CreateDesignTool from "../pages/tools/CreateDesignTool";
import CreateIdeaTool from "../pages/tools/CreateIdeaTool";
import CreateStencilTool from "../pages/tools/CreateStencilTool";
import TextDesignTool from "../pages/tools/TextDesignTool";

const ROUTES = {
  "/": "design",
  "/tattoo-design-generator": "design",
  "/tattoo-ideas": "idea",
  "/tattoo-ideas-generator": "idea",
  "/tattoo-ideas-generator/result": "idea",
  "/tattoo-stencil-maker": "stencil",
  "/tattoo-ideas/coverup": "coverup",
  "/tattoo-ideas/filler": "filler",
  "/tattoo-fonts-generator": "font",
};

const PAGES = {
  design: <CreateDesignTool />,
  idea: <CreateIdeaTool />,
  stencil: <CreateStencilTool />,
  coverup: <MaskDrawingGuide type="coverup" />,
  filler: <MaskDrawingGuide type="filler" />,
  font: <TextDesignTool />,
};

const CreateRouter = (props) => {
  const theme = useTheme();
  const location = useLocation();
  const activeKey =
    ROUTES[location.pathname] || (location.pathname.includes("font") ? "font" : null);
  const menuListRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);

  // Create refs for each menu item
  const menuRefs = {
    design: useRef(null),
    idea: useRef(null),
    stencil: useRef(null),
    coverup: useRef(null),
    filler: useRef(null),
    font: useRef(null),
  };

  const handleScroll = (e) => {
    const element = e.target;
    const isEnd = element.scrollLeft + element.clientWidth >= element.scrollWidth - 10;
    setShowScrollIndicator(!isEnd);
  };

  const scrollRight = () => {
    if (menuListRef.current) {
      menuListRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  // Scroll to the active item on component mount
  useEffect(() => {
    if (menuRefs[activeKey] && menuRefs[activeKey].current) {
      menuRefs[activeKey].current.scrollIntoView({
        behavior: "instant",
        inline: "nearest",
        block: "nearest",
      });
    }
  }, [activeKey]);

  return (
    <Section
      bgColor={props.bgColor}
      size="auto"
      bgImage={props.bgImage}
      bgImageOpacity={props.bgImageOpacity}
    >
      <Stack direction={{ xs: "column", md: "row" }}>
        <Box>
          <Box
            sx={{
              position: "relative",
              height: { md: "100%" },
              [`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  right: 0,
                  height: "100%",
                  width: "40px",
                  background:
                    "linear-gradient(to right, transparent, rgba(255,255,255,0.9) 50%, rgba(255,255,255,1))",
                  pointerEvents: "none",
                  opacity: showScrollIndicator ? 1 : 0,
                  transition: "opacity 0.2s",
                },
              },
            }}
          >
            <Box
              onClick={scrollRight}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 1,
                opacity: showScrollIndicator ? 0.4 : 0,
                display: { xs: "block", md: "none" },
                cursor: showScrollIndicator ? "pointer" : "default",
                pointerEvents: showScrollIndicator ? "auto" : "none",
                transition: "opacity 0.2s",
                WebkitTapHighlightColor: "transparent",
                WebkitTouchCallout: "none",
                userSelect: "none",
                "&:hover": {
                  opacity: showScrollIndicator ? 0.7 : 0,
                },
              }}
            >
              <ChevronRightIcon />
            </Box>
            <MenuList
              ref={menuListRef}
              onScroll={handleScroll}
              sx={{
                display: "flex",
                flexGrow: 1,
                flexDirection: { xs: "row", md: "column" },
                width: "100%",
                height: "100%",
                py: 0,
                borderRight: { xs: 0, md: `1px solid ${theme.palette.divider}` },
                scrollBehavior: "smooth",
                "& .MuiMenuItem-root": {
                  display: "flex",
                  flexDirection: "column",
                  "& .MuiSvgIcon-root": {},
                  ":hover, &.Mui-selected": {
                    color: theme.palette.primary.main,
                  },
                },
                // Mobile horizontal scroll for active items
                [`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
                  overflowX: "auto",
                  display: "flex",
                  flexDirection: "row",
                  "& .MuiMenuItem-root": {
                    minWidth: 130,
                    flex: 1,
                    justifyContent: "center",
                  },
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                },
              }}
            >
              <MenuItem
                ref={menuRefs.design}
                color="primary"
                component={Link as React.ElementType}
                to="/tattoo-design-generator"
                value="design"
                selected={activeKey === "design" || !activeKey}
                divider
              >
                <CreateIcon />
                AI Designer
              </MenuItem>

              <MenuItem
                ref={menuRefs.font}
                component={Link as React.ElementType}
                to="/tattoo-fonts-generator"
                value="font"
                selected={activeKey === "font"}
                divider
              >
                <FontDownload />
                Font Generator
              </MenuItem>

              <MenuItem
                ref={menuRefs["coverup"]}
                component={Link as React.ElementType}
                to="/tattoo-ideas/coverup"
                value="coverup"
                selected={activeKey === "coverup"}
                divider
              >
                <CoverupIcon />
                Cover Up
              </MenuItem>

              <MenuItem
                ref={menuRefs.filler}
                component={Link as React.ElementType}
                to="/tattoo-ideas/filler"
                value="filler"
                selected={activeKey === "filler"}
                divider
              >
                <FillerIcon />
                Gap Filler
              </MenuItem>

              <MenuItem
                ref={menuRefs.stencil}
                component={Link as React.ElementType}
                to="/tattoo-stencil-maker"
                value="stencil"
                selected={activeKey === "stencil"}
                divider
              >
                <OutlineIcon />
                Stencil Maker
              </MenuItem>

              <MenuItem
                ref={menuRefs.idea}
                component={Link as React.ElementType}
                to="/tattoo-ideas-generator"
                value="idea"
                selected={activeKey === "idea"}
                divider
              >
                <IdeaIcon />
                Idea Generator
              </MenuItem>
            </MenuList>
          </Box>
        </Box>
        <Container sx={{ pt: 1, px: 2 }} maxWidth="lg">
          {PAGES[activeKey]}
        </Container>
      </Stack>
    </Section>
  );
};

export default CreateRouter;
